import React, { useState, useEffect, useRef } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/authcontext'
import { CreateUser, LoginAndGetDashboard, sendMsg91Otp, verifyMsg91Otp, retryMsg91Otp, GetUserByMobileAndRoleID, generateOtpmsg91 } from '../apis/services/pgApi'
import SearchBar from './searchbar'
import { FaRegCircleUser } from "react-icons/fa6";
import { useUI } from '../context/uicontext'
import ForgotPasswordModal from './ForgotPasswordModal'

// ─── MSG91 OTP Configuration ──────────────────────────────────────
const MSG91_CONFIG = {
  OTP_LENGTH: 4, // 4 digits as per your settings
  RESEND_AFTER: 10, // 10 seconds
  EXPIRY_TIME: 15, // 15 minutes
  CHANNELS: 'OTP', // SMS/OTP
  VERIFICATION_TYPE: '4 digits'
}

// ─── OTP Input — 4 individual boxes ──────────────────────────────
const OTPInput = ({ value, onChange, length = 4 }) => {
  const inputs = useRef([])

  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !e.target.value && i > 0) {
      inputs.current[i - 1]?.focus()
    }
  }

  const handleChange = (i, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1)
    const arr = value.split('')
    arr[i] = digit
    const next = arr.join('').padEnd(length, ' ').slice(0, length)
    onChange(next.trimEnd())
    if (digit && i < length - 1) inputs.current[i + 1]?.focus()
  }

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (paste) {
      onChange(paste)
      inputs.current[Math.min(paste.length, length - 1)]?.focus()
    }
    e.preventDefault()
  }

  return (
    <div className="otp-row">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={el => inputs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className={`otp-box ${value[i] && value[i] !== ' ' ? 'filled' : ''}`}
          value={value[i] && value[i] !== ' ' ? value[i] : ''}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          autoFocus={i === 0}
        />
      ))}
    </div>
  )
}

// ─── Timer Hook ────────────────────────────────────────────────────
const useTimer = (initialSeconds = 10) => {
  const [seconds, setSeconds] = useState(initialSeconds)
  const [isRunning, setIsRunning] = useState(false)
  const timerRef = useRef(null)

  const startTimer = () => {
    setIsRunning(true)
    setSeconds(initialSeconds)
  }

  useEffect(() => {
    if (isRunning && seconds > 0) {
      timerRef.current = setTimeout(() => {
        setSeconds(prev => prev - 1)
      }, 1000)
    } else if (seconds === 0) {
      setIsRunning(false)
      clearTimeout(timerRef.current)
    }
    return () => clearTimeout(timerRef.current)
  }, [isRunning, seconds])

  return { seconds, isRunning, startTimer }
}

// ─── Auth Modal ────────────────────────────────────────────────────
const AuthModal = ({ onClose }) => {
  const navigate = useNavigate()
  const { login } = useAuth()
  const overlayRef = useRef(null)

  const [authType, setAuthType] = useState('TENANT')
  const [authMode, setAuthMode] = useState('login')
  const [step, setStep] = useState('form')
  const [loginMethod, setLoginMethod] = useState('password')
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [selectedRole, setSelectedRole] = useState('OWNER')

  // Signup form fields
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')

  // OTP
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [otpExpiryTimer, setOtpExpiryTimer] = useState(null)
  const [otpExpired, setOtpExpired] = useState(false)

  // Timer for resend
  const { seconds: resendCooldown, isRunning: resendCooldownActive, startTimer: startResendCooldown } = useTimer(MSG91_CONFIG.RESEND_AFTER)

  // Timer for OTP expiry (15 minutes)
  useEffect(() => {
    let expiryInterval = null
    if (otpSent && step === 'otp') {
      let timeLeft = MSG91_CONFIG.EXPIRY_TIME * 60 // 15 minutes in seconds
      setOtpExpired(false)

      expiryInterval = setInterval(() => {
        timeLeft -= 1
        if (timeLeft <= 0) {
          setOtpExpired(true)
          setError('OTP has expired. Please request a new one.')
          clearInterval(expiryInterval)
        }
        // Update timer display if needed
        const minutes = Math.floor(timeLeft / 60)
        const seconds = timeLeft % 60
        setOtpExpiryTimer(`${minutes}:${seconds.toString().padStart(2, '0')}`)
      }, 1000)
    }
    return () => clearInterval(expiryInterval)
  }, [otpSent, step])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose() }

  // ✅ Send OTP using MSG91
  const sendOTP = async (mobileNumber) => {
    try {
      setLoading(true)
      setError('')
      setOtpExpired(false)

      const response = await sendMsg91Otp({
        mobile: `91${mobileNumber}`,
        authkey: '522317Abg4sY6n6a2fb1ebP1',
        template_id: '6a35297075006cf37e02f3f2',
        param1: 'OTP',
        param2: 'value2',
        param3: 'value3'
      })

      console.log('MSG91 Send OTP Response:', response)

      if (response.success) {
        setOtpSent(true)
        setOtp('')
        setStep('otp')
        startResendCooldown() // Start 10 second cooldown
        return true
      } else {
        setError(response.message || 'Failed to send OTP. Please try again.')
        return false
      }
    } catch (err) {
      console.error('Send OTP error:', err)
      setError(err.message || 'Failed to send OTP. Please try again.')
      return false
    } finally {
      setLoading(false)
    }
  }

  // ✅ Verify OTP using MSG91
  const verifyOTP = async (mobileNumber, otpCode) => {
    try {
      setLoading(true)
      setError('')

      const response = await verifyMsg91Otp(`91${mobileNumber}`, otpCode)
      console.log('MSG91 Verify OTP Response:', response)

      if (response.success) {
        return true
      } else {
        setError(response.message || 'Invalid OTP. Please try again.')
        return false
      }
    } catch (err) {
      console.error('Verify OTP error:', err)
      setError(err.message || 'OTP verification failed. Please try again.')
      return false
    } finally {
      setLoading(false)
    }
  }

  // ✅ Resend OTP using MSG91
  const resendOTP = async (mobileNumber) => {
    if (resendCooldownActive) {
      setError(`Please wait ${resendCooldown} seconds before requesting again.`)
      return
    }

    try {
      setLoading(true)
      setError('')

      const response = await retryMsg91Otp(`91${mobileNumber}`, 'text')
      console.log('MSG91 Resend OTP Response:', response)

      if (response.success) {
        setOtp('')
        setOtpExpired(false)
        setError('')
        startResendCooldown() // Reset cooldown timer
        alert('OTP resent successfully!')
        return true
      } else {
        setError(response.message || 'Failed to resend OTP. Please try again.')
        return false
      }
    } catch (err) {
      console.error('Resend OTP error:', err)
      setError(err.message || 'Failed to resend OTP. Please try again.')
      return false
    } finally {
      setLoading(false)
    }
  }


  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    if (authMode === 'signup') {
      if (!name.trim()) { setError('Please enter your full name.'); setLoading(false); return }
      if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) { setError('Please enter a valid email.'); setLoading(false); return }
      if (!/^\d{10}$/.test(phone.trim())) { setError('Please enter a valid 10-digit phone number.'); setLoading(false); return }
      if (!password.trim()) { setError('Please enter a password.'); setLoading(false); return }

      let roleId;
      if (authType === 'PG') {
        roleId = 2; // Always OWNER for signup
        setSelectedRole('OWNER');
      } else {
        roleId = 3; // TENANT
      }

      // ✅ NEW: Send OTP for signup
      try {
        setLoading(true)

        console.log("Checking if user exists for signup...", phone.trim(), roleId);

        const userCheckResponse = await GetUserByMobileAndRoleID(phone.trim(), roleId);
        console.log('User check response for signup:', userCheckResponse);

        // ❌ If user ALREADY exists - Stop and show error
        if (userCheckResponse.success) {
          // User found with this mobile and role
          const roleName = authType === 'PG' ? 'PG Owner' : 'Tenant';
          setError(
            `❌ Mobile number "${phone.trim()}" is already registered as a ${roleName}. ` +
            `Please login instead.`
          );
          setLoading(false);
          return; // 🔴 STOP - Don't send OTP
        }


        const otpSentSuccess = await sendOTP(phone.trim())
        if (otpSentSuccess) {
          setOtp('')
          setStep('otp')
          setOtpSent(true)
        }
      } catch (err) {
        console.error('Send OTP error:', err)
        setError('Failed to send OTP. Please try again.')
      } finally {
        setLoading(false)
      }
      return
    }


    // LOGIN MODE
    if (!/^\d{10}$/.test(phone.trim())) {
      setError('Please enter a valid 10-digit phone number.');
      setLoading(false);
      return
    }

    if (loginMethod === 'password') {
      // Password login flow
      if (!password.trim()) {
        setError('Please enter your password.');
        setLoading(false);
        return
      }

      try {
        let apiType = authType.toUpperCase()
        if (authType === 'PG') {
          if (selectedRole === 'OWNER') apiType = 'PG'
          else if (selectedRole === 'STAFF') apiType = 'STAFF'
          else if (selectedRole === 'SUPPORTING STAFF') apiType = 'SUPPORTING STAFF'
        }

        const response = await LoginAndGetDashboard({
          PhoneNumber: phone,
          PasswordHash: password,
          OTPCode: null,
          Type: apiType
        })

        if (response && response[0] && response[0][0]) {
          const loginResult = response[0][0]

          if (loginResult.Success === 0) {
            setError(loginResult.Message || 'Invalid credentials. Please try again.')
            setLoading(false)
            return
          }

          if (authType === 'PG') {
            if (selectedRole === 'OWNER' && loginResult.Role !== 2) {
              setError('You are not registered as a PG Owner.')
              setLoading(false)
              return
            }
            if (selectedRole === 'STAFF' && loginResult.Role !== 4) {
              setError('You are not registered as Staff.')
              setLoading(false)
              return
            }
            if (selectedRole === 'SUPPORTING STAFF' && loginResult.Role !== 5) {
              setError('You are not registered as Supporting Staff.')
              setLoading(false)
              return
            }
          } else if (authType === 'TENANT' && loginResult.Role !== 3) {
            setError('You are not registered as a Tenant.')
            setLoading(false)
            return
          }

          localStorage.setItem('loginResponse', JSON.stringify(response))
          localStorage.setItem('userId', loginResult.UserID)
          localStorage.setItem('userRole', loginResult.Role)
          localStorage.setItem('username', phone.trim())
          localStorage.setItem('password', password.trim())

          const loginInfo = response[0]?.[0] || {}
          const userDataList = response[1] || []

          if (userDataList.length > 0) {
            localStorage.setItem('userDataList', JSON.stringify(userDataList))
          }

          const tenantDataFromAPI = userDataList[0] || {}

          const userData = {
            id: loginResult.UserID,
            name: tenantDataFromAPI.TenantName || loginInfo.Name || name || 'User',
            phone: tenantDataFromAPI.PhoneNumber || phone.trim(),
            email: tenantDataFromAPI.Email1 || email || loginInfo.Email || '',
            type: authType.toLowerCase(),
            role: loginResult.Role
          }

          let tenantInfo = null
          if (authType === 'TENANT' && loginResult.Role === 3) {
            tenantInfo = {
              TenantID: tenantDataFromAPI.TenantID || loginResult.UserID,
              TenantName: tenantDataFromAPI.TenantName || loginInfo.Name || name || 'User',
              Mobile: tenantDataFromAPI.PhoneNumber || phone.trim(),
              Email: tenantDataFromAPI.Email1 || email || loginInfo.Email || '',
              RoomNo: tenantDataFromAPI.RoomNo || '',
              BedID: tenantDataFromAPI.BedID || '',
              RentAmount: tenantDataFromAPI.RentAmount || 0,
              CheckInDate: tenantDataFromAPI.CheckInDate || tenantDataFromAPI.JoiningDate || '',
              PGID: tenantDataFromAPI.PGID || null,
              Address: tenantDataFromAPI.PresentAddress || tenantDataFromAPI.PermanentAddress || '',
              Company: tenantDataFromAPI.TenantProfession || '',
              GuardianName: tenantDataFromAPI.EmergencyContactPersonName || '',
              GuardianMobile: tenantDataFromAPI.EmergencyContactNumber || '',
              Relation: tenantDataFromAPI.EmergencyContactRelation || '',
              TenantCode: tenantDataFromAPI.TenantCode || '',
              Status: tenantDataFromAPI.Status || '',
              IsActive: tenantDataFromAPI.IsActive || false,
              Gender: tenantDataFromAPI.Gender || '',
              TenantDOB: tenantDataFromAPI.TenantDOB || '',
              TenantAge: tenantDataFromAPI.Tenantage || '',
            }
          }

          login(userData, tenantInfo, userDataList)
          onClose()
          if (authType === 'PG') navigate('/owner-dashboard')
          else navigate('/tenant-dashboard')
        } else {
          setError('Invalid response from server. Please try again.')
        }
      } catch (err) {
        setError(err.message || 'Login failed. Please check your credentials.')
      } finally {
        setLoading(false)
      }
    } else {
      // OTP LOGIN FLOW - Check if user exists before sending OTP
      try {
        setLoading(true)

        // Determine role ID based on selected role
        let roleId;
        if (authType === 'PG') {
          if (selectedRole === 'OWNER') roleId = 2;
          else if (selectedRole === 'STAFF') roleId = 4;
          else if (selectedRole === 'SUPPORTING STAFF') roleId = 5;
        } else if (authType === 'TENANT') {
          roleId = 3;
        }

        console.log("selectedRole", selectedRole);

        // Check if user exists with this mobile number and role
        const userCheckResponse = await GetUserByMobileAndRoleID(phone.trim(), roleId);
        console.log('User check response:', userCheckResponse);

        if (!userCheckResponse.success) {
          // User not found
          setError(`No account found with this mobile number for ${authType == 'PG' ? selectedRole : authType}. Please sign up first.`);
          setLoading(false);
          return;
        }

        // User exists - send OTP
        const otpSentSuccess = await sendOTP(phone.trim())
        if (otpSentSuccess) {
          setOtp('')
          setStep('otp')
          setOtpSent(true)
        }
      } catch (err) {
        console.error('User check error:', err);
        setError(err.message || 'Failed to verify user. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }


  const handleOTPSubmit = async () => {
    console.log("Verifying OTP for signup...", authType)

    const cleaned = otp.replace(/\s/g, '')
    if (cleaned.length !== MSG91_CONFIG.OTP_LENGTH) {
      setError(`Please enter the ${MSG91_CONFIG.OTP_LENGTH}-digit OTP.`);
      return
    }

    if (otpExpired) {
      setError('OTP has expired. Please request a new one.')
      return
    }

    // For signup mode
    if (authMode === 'signup') {
      const isVerified = await verifyOTP(phone.trim(), cleaned);
      if (!isVerified) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)

        let roleId;
        if (authType === 'PG') {
          roleId = 2; // Always OWNER for signup
        } else {
          roleId = 3; // TENANT
        }

        const userData = {
          FullName: name.trim(),
          UserName: phone.trim(),
          Password: password,
          MobileNo: phone.trim(),
          EmailId: email.trim(),
          RoleId: roleId,
          PGID: null
        };

        console.log("Creating user with data:", userData);

        const response = await CreateUser(userData);
        console.log("User creation response:", response);


        if (!response.success) {
          if (response.error === 'DUPLICATE_ENTRY') {
            // User-friendly error message
            setError(response.message); // "Mobile number "6260216707" is already registered as a Tenant. Please use a different number or login."
            setName('');
            setEmail('');
            setPhone('');
            setPassword('');
            setOtp('');
            setStep('form');
            setAuthMode('signup');
            setOtpSent(false);
            setLoading(false);
            return;
          } else {
            setError(response.message || 'Signup failed. Please try again.');
            setName('');
            setEmail('');
            setPhone('');
            setPassword('');
            setOtp('');
            setStep('form');
            setAuthMode('signup');
            setOtpSent(false);
            setLoading(false);
            return;
          }
        }


        setError('');
        onClose();
        alert(`Account created successfully! Login credentials have been sent to ${email}. Please check your email and login.`);

        setName('');
        setEmail('');
        setPhone('');
        setPassword('');
        setOtp('');
        setStep('form');
        setAuthMode('login');
        setOtpSent(false);

      } catch (err) {
        console.error("Signup error:", err);
        setError(err.message || 'Signup failed. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // Login with OTP flow
      const isVerified = await verifyOTP(phone.trim(), cleaned)

      if (!isVerified) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)


        // ✅ Generate OTP in database using MSG91 stored procedure
        // This will store the OTP in the database before login
        try {
          let roleId;
          if (authType === 'PG') {
            if (selectedRole === 'OWNER') roleId = 2;
            else if (selectedRole === 'STAFF') roleId = 4;
            else if (selectedRole === 'SUPPORTING STAFF') roleId = 5;
          } else if (authType === 'TENANT') {
            roleId = 3;
          }

          // Get PGID and ReferenceID if needed
          let pgid = null;
          let referenceId = null;

          // Call generateOtpmsg91 to store OTP in database
          const generateOtpResponse = await generateOtpmsg91(
            phone.trim(),      // mobileNumber
            'Login',           // purpose
            cleaned,           // otp
            pgid,              // pgid (null if not available)
            referenceId        // referenceId (null if not available)
          );

          console.log('Generate OTP in DB Response:', generateOtpResponse);

          if (!generateOtpResponse.success) {
            console.warn('OTP generation in DB failed:', generateOtpResponse.message);
            // Continue with login even if DB OTP generation fails
          } else {
            console.log('OTP stored in database successfully:', generateOtpResponse.data);
          }
        } catch (dbError) {
          console.error('Error generating OTP in database:', dbError);
          // Continue with login even if DB OTP generation fails
        }


        let apiType = authType.toUpperCase()
        if (authType === 'PG') {
          if (selectedRole === 'OWNER') apiType = 'PG'
          else if (selectedRole === 'STAFF') apiType = 'STAFF'
          else if (selectedRole === 'SUPPORTING STAFF') apiType = 'SUPPORTING STAFF'
        }

        const response = await LoginAndGetDashboard({
          PhoneNumber: phone.trim(),
          PasswordHash: null,
          OTPCode: cleaned,
          Type: apiType
        })

        if (response && response[0] && response[0][0]) {
          const loginResult = response[0][0]

          if (loginResult.Success === 0) {
            setError(loginResult.Message || 'Invalid OTP. Please try again.')
            setLoading(false)
            return
          }

          if (authType === 'PG') {
            if (selectedRole === 'OWNER' && loginResult.Role !== 2) {
              setError('You are not registered as a PG Owner.')
              setLoading(false)
              return
            }
            if (selectedRole === 'STAFF' && loginResult.Role !== 4) {
              setError('You are not registered as Staff.')
              setLoading(false)
              return
            }
            if (selectedRole === 'SUPPORTING STAFF' && loginResult.Role !== 5) {
              setError('You are not registered as Supporting Staff.')
              setLoading(false)
              return
            }
          } else if (authType === 'TENANT' && loginResult.Role !== 3) {
            setError('You are not registered as a Tenant.')
            setLoading(false)
            return
          }

          localStorage.setItem('loginResponse', JSON.stringify(response))
          localStorage.setItem('userId', loginResult.UserID)
          localStorage.setItem('userRole', loginResult.Role)
          localStorage.setItem('username', phone.trim())

          const loginInfo = response[0]?.[0] || {}
          const userDataList = response[1] || []

          if (userDataList.length > 0) {
            localStorage.setItem('userDataList', JSON.stringify(userDataList))
          }

          const tenantDataFromAPI = userDataList[0] || {}

          const userData = {
            id: loginResult.UserID,
            name: tenantDataFromAPI.TenantName || loginInfo.Name || name || 'User',
            phone: tenantDataFromAPI.PhoneNumber || phone.trim(),
            email: tenantDataFromAPI.Email1 || email || loginInfo.Email || '',
            type: authType.toLowerCase(),
            role: loginResult.Role
          }

          let tenantInfo = null
          if (authType === 'TENANT' && loginResult.Role === 3) {
            tenantInfo = {
              TenantID: tenantDataFromAPI.TenantID || loginResult.UserID,
              TenantName: tenantDataFromAPI.TenantName || loginInfo.Name || name || 'User',
              Mobile: tenantDataFromAPI.PhoneNumber || phone.trim(),
              Email: tenantDataFromAPI.Email1 || email || loginInfo.Email || '',
              RoomNo: tenantDataFromAPI.RoomNo || '',
              BedID: tenantDataFromAPI.BedID || '',
              RentAmount: tenantDataFromAPI.RentAmount || 0,
              CheckInDate: tenantDataFromAPI.CheckInDate || tenantDataFromAPI.JoiningDate || '',
              PGID: tenantDataFromAPI.PGID || null,
              Address: tenantDataFromAPI.PresentAddress || tenantDataFromAPI.PermanentAddress || '',
              Company: tenantDataFromAPI.TenantProfession || '',
              GuardianName: tenantDataFromAPI.EmergencyContactPersonName || '',
              GuardianMobile: tenantDataFromAPI.EmergencyContactNumber || '',
              Relation: tenantDataFromAPI.EmergencyContactRelation || '',
              TenantCode: tenantDataFromAPI.TenantCode || '',
              Status: tenantDataFromAPI.Status || '',
              IsActive: tenantDataFromAPI.IsActive || false,
              Gender: tenantDataFromAPI.Gender || '',
              TenantDOB: tenantDataFromAPI.TenantDOB || '',
              TenantAge: tenantDataFromAPI.Tenantage || '',
            }
          }

          login(userData, tenantInfo, userDataList)
          onClose()
          if (authType === 'PG') navigate('/owner-dashboard')
          else navigate('/tenant-dashboard')
        } else {
          setError('Invalid response from server. Please try again.')
        }
      } catch (err) {
        setError(err.message || 'Invalid OTP. Please try again.')
      } finally {
        setLoading(false)
      }
    }
  }



  const switchMode = (mode) => {
    setAuthMode(mode)
    setStep('form')
    setName(''); setEmail(''); setPhone(''); setPassword(''); setOtp(''); setError('')
    setLoginMethod('password')
    if (mode === 'signup' && authType === 'PG') {
      setSelectedRole('OWNER')
    } else {
      setSelectedRole('OWNER') // Default for both
    }
    setOtpSent(false)
    setOtpExpired(false)
  }

  const toggleLoginMethod = () => {
    setLoginMethod(loginMethod === 'password' ? 'otp' : 'password')
    setError('')
    setPassword('')
    setOtp('')
    setOtpSent(false)
    setOtpExpired(false)
  }

  const handleResendOTP = async () => {
    if (!phone.trim()) {
      setError('Phone number is required')
      return
    }
    await resendOTP(phone.trim())
  }

  // Format expiry time display
  const getExpiryDisplay = () => {
    if (!otpExpiryTimer) return `${MSG91_CONFIG.EXPIRY_TIME}:00`
    return otpExpiryTimer
  }

  return (
    <>
      <div className="auth-overlay" ref={overlayRef} onClick={handleOverlayClick}>
        <div className="auth-modal">
          <button className="auth-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          {step === 'otp' && (
            <button className="auth-back" onClick={() => { setStep('form'); setError(''); setOtpSent(false); setOtpExpired(false) }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M13 4L7 10L13 16" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Back
            </button>
          )}

          <div className="auth-header">
            {step === 'form' ? (
              <>
                <h2>{authMode === 'login' ? 'Welcome back' : 'Create account'}</h2>
                <p>{authMode === 'login' ? 'Enter your credentials to continue.' : 'Sign up to explore the best PGs near you.'}</p>
              </>
            ) : (
              <>
                <h2>{authMode === 'signup' ? 'Verify Your Number' : 'Enter OTP'}</h2>
                <p>
                  {authMode === 'signup'
                    ? `We've sent a verification code to ${phone}`
                    : `We've sent a ${MSG91_CONFIG.OTP_LENGTH}-digit code to ${phone}`}
                </p>
              </>
            )}
          </div>

          {step === 'form' && (
            <div className="auth-type-toggle">
              <button className={authType === 'TENANT' ? 'active' : ''} onClick={() => setAuthType('TENANT')}>I'm a Tenant</button>
              <button className={authType === 'PG' ? 'active' : ''} onClick={() => setAuthType('PG')}>I'm a PG Owner</button>
            </div>
          )}

          {/* ── FORM STEP ── */}
          {step === 'form' && (
            <form className="auth-form" onSubmit={handleFormSubmit} noValidate>
              {authMode === 'signup' && (
                <>
                  <div className="auth-field">
                    <label>Full Name</label>
                    <input type="text" placeholder="Rahul Sharma" value={name} onChange={e => { setName(e.target.value); setError('') }} autoComplete="name" />
                  </div>
                  <div className="auth-field">
                    <label>Email Address</label>
                    <input type="email" placeholder="you@example.com" value={email} onChange={e => { setEmail(e.target.value); setError('') }} autoComplete="email" />
                  </div>
                </>
              )}

              {authType === 'PG' && authMode === 'login' ? (
                <div className="auth-row-two-columns">
                  <div className="auth-field">
                    <label>Role</label>
                    <select
                      className="auth-role-select-half"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                    >
                      <option value="OWNER">Owner</option>
                      <option value="STAFF">Staff</option>
                      <option value="SUPPORTING STAFF">Supporting Staff</option>
                    </select>
                  </div>
                  <div className="auth-field">
                    <label>Phone Number</label>
                    <div className="auth-phone-row">
                      <input
                        type="tel"
                        placeholder="10-digit mobile number"
                        value={phone}
                        onChange={e => { setPhone(e.target.value.replace(/\D/g, '')); setError('') }}
                        maxLength={10}
                        autoComplete="tel"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                // For TENANT or PG SIGNUP, show only phone number
                <div className="auth-field">
                  <label>Phone Number</label>
                  <div className="auth-phone-row">
                    <input
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={phone}
                      onChange={e => { setPhone(e.target.value.replace(/\D/g, '')); setError('') }}
                      maxLength={10}
                      autoComplete="tel"
                    />
                  </div>
                </div>
              )}

              {(authMode === 'login' && loginMethod === 'password') || authMode === 'signup' ? (
                <div className="auth-field">
                  <label>{authMode === 'signup' ? 'Create Password' : 'Password'}</label>
                  <input
                    type="password"
                    placeholder={authMode === 'signup' ? 'Create a strong password' : "Enter your password"}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError('') }}
                    autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                  />
                </div>
              ) : null}

              {error && <p className="auth-error">{error}</p>}

              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Please wait...' : (authMode === 'login' ? (loginMethod === 'password' ? 'Log In' : 'Send OTP') : 'Continue')}
              </button>

              {authMode === 'login' && step === 'form' && (
                <div className="auth-links-row">
                  <span onClick={toggleLoginMethod} className="auth-switch-link">
                    {loginMethod === 'password' ? 'Login with OTP' : 'Back to Password Login'}
                  </span>
                  {loginMethod === 'password' && (
                    <span
                      onClick={() => setShowForgotPassword(true)}
                      className="auth-switch-link"
                    >
                      Forgot Password?
                    </span>
                  )}
                </div>
              )}
            </form>
          )}

          {/* ── OTP STEP ── */}
          {step === 'otp' && (
            <div className="auth-otp-section">
              <p className="auth-otp-hint" style={{ textAlign: 'center', marginBottom: '10px' }}>
                {authMode === 'signup'
                  ? `Please enter the ${MSG91_CONFIG.OTP_LENGTH}-digit verification code`
                  : `Enter the ${MSG91_CONFIG.OTP_LENGTH}-digit OTP sent to your phone`}
              </p>

              <OTPInput value={otp} onChange={setOtp} length={MSG91_CONFIG.OTP_LENGTH} />

              {/* OTP Info */}
              <div className="otp-info-container">
                <div className="otp-expiry-info">
                  <span className="otp-expiry-label">⏱️ Expires in:</span>
                  <span className={`otp-expiry-timer ${otpExpired ? 'expired' : ''}`}>
                    {otpExpired ? 'Expired' : getExpiryDisplay()}
                  </span>
                </div>
                <div className="otp-resend-info">
                  <span className="otp-resend-label">Resend after:</span>
                  <span className="otp-resend-timer">
                    {resendCooldownActive ? `${resendCooldown}s` : 'Ready'}
                  </span>
                </div>
              </div>

              <p className="auth-otp-hint">
                Didn't receive it?{' '}
                <span
                  className={`auth-resend ${resendCooldownActive || loading ? 'disabled' : ''}`}
                  onClick={handleResendOTP}
                >
                  {resendCooldownActive ? `Wait ${resendCooldown}s` : 'Resend Code'}
                </span>
              </p>

              {error && <p className="auth-error">{error}</p>}

              <button
                className="auth-submit"
                onClick={handleOTPSubmit}
                disabled={loading || otpExpired}
              >
                {loading ? 'Please wait...' : (authMode === 'signup' ? 'Verify & Create Account' : 'Verify & Login')}
              </button>

              {otpExpired && (
                <p className="auth-warning" style={{ color: '#DC2626', fontSize: '13px', marginTop: '8px' }}>
                  ⚠️ OTP has expired. Please click "Resend Code" to get a new OTP.
                </p>
              )}
            </div>
          )}

          <p className="auth-switch">
            {authMode === 'login'
              ? <>Don't have an account?{' '}<span onClick={() => switchMode('signup')}>Sign up</span></>
              : <>Already have an account?{' '}<span onClick={() => switchMode('login')}>Log in</span></>
            }
          </p>
        </div>
      </div>

      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </>
  )
}

// ─── ListPGModal ──────────────────────────────────────────────────
const ListPGModal = ({ onClose }) => {
  const overlayRef = useRef(null)
  const [step, setStep] = useState(1)
  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    pgName: '', pgAddress: '', pgCity: '',
    gender: '', sharing: [], price: '', amenities: [], about: '',
  })
  const [error, setError] = useState('')

  const SHARING_OPTIONS = ['Single', 'Double', 'Triple']
  const AMENITY_OPTIONS = ['Wi-Fi', 'AC', 'Food', 'Laundry', 'Parking', 'CCTV', 'Power Backup', 'Housekeeping']

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose() }
  const handleInput = (field) => (e) => { setForm(p => ({ ...p, [field]: e.target.value })); setError('') }
  const toggleArray = (field, val) => {
    setForm(p => ({ ...p, [field]: p[field].includes(val) ? p[field].filter(v => v !== val) : [...p[field], val] }))
  }

  const validateStep1 = () => {
    if (!form.name.trim()) return 'Please enter your full name.'
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) return 'Please enter a valid email.'
    if (!/^\d{10}$/.test(form.phone.trim())) return 'Please enter a valid 10-digit phone number.'
    return ''
  }
  const validateStep2 = () => {
    if (!form.pgName.trim()) return 'Please enter your PG name.'
    if (!form.pgAddress.trim()) return 'Please enter the PG address.'
    if (!form.gender) return 'Please select gender type.'
    if (form.sharing.length === 0) return 'Please select at least one sharing type.'
    if (!form.price.trim()) return 'Please enter a starting price.'
    return ''
  }

  return (
    <div className="list-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="list-modal">
        <div className="list-modal-header">
          {step < 3 && (
            <>
              <div>
                <h2 className="list-modal-title">List your PG</h2>
                <p className="list-modal-sub">Step {step} of 2 — {step === 1 ? 'Your details' : 'PG details'}</p>
              </div>
              <div className="list-progress">
                <div className={`list-progress-dot ${step >= 1 ? 'done' : ''}`} />
                <div className="list-progress-line" />
                <div className={`list-progress-dot ${step >= 2 ? 'done' : ''}`} />
              </div>
            </>
          )}
          <button className="list-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="list-modal-body">
          {step === 1 && (
            <div className="list-fields">
              <div className="list-field"><label>Full Name *</label><input type="text" placeholder="Rajesh Sharma" value={form.name} onChange={handleInput('name')} /></div>
              <div className="list-field"><label>Email Address *</label><input type="email" placeholder="you@example.com" value={form.email} onChange={handleInput('email')} /></div>
              <div className="list-field"><label>Phone Number *</label><input type="tel" placeholder="10-digit mobile number" value={form.phone} onChange={handleInput('phone')} maxLength={10} /></div>
            </div>
          )}
          {step === 2 && (
            <div className="list-fields">
              <div className="list-field"><label>PG Name *</label><input type="text" placeholder="e.g. Sharma Living PG" value={form.pgName} onChange={handleInput('pgName')} /></div>
              <div className="list-field"><label>Address *</label><input type="text" placeholder="Street, Area, Landmark" value={form.pgAddress} onChange={handleInput('pgAddress')} /></div>
              <div className="list-field"><label>City</label><input type="text" placeholder="New Delhi" value={form.pgCity} onChange={handleInput('pgCity')} /></div>
              <div className="list-field">
                <label>Gender *</label>
                <div className="list-chip-row">
                  {['Boys', 'Girls', 'Co-ed'].map(g => <button key={g} type="button" className={`list-chip ${form.gender === g ? 'selected' : ''}`} onClick={() => { setForm(p => ({ ...p, gender: g })); setError('') }}>{g}</button>)}
                </div>
              </div>
              <div className="list-field">
                <label>Sharing Types *</label>
                <div className="list-chip-row">
                  {SHARING_OPTIONS.map(s => <button key={s} type="button" className={`list-chip ${form.sharing.includes(s) ? 'selected' : ''}`} onClick={() => toggleArray('sharing', s)}>{s}</button>)}
                </div>
              </div>
              <div className="list-field"><label>Starting Price (₹/month) *</label><input type="number" placeholder="e.g. 8000" value={form.price} onChange={handleInput('price')} /></div>
              <div className="list-field">
                <label>Amenities</label>
                <div className="list-chip-row list-chip-wrap">
                  {AMENITY_OPTIONS.map(a => <button key={a} type="button" className={`list-chip ${form.amenities.includes(a) ? 'selected' : ''}`} onClick={() => toggleArray('amenities', a)}>{a}</button>)}
                </div>
              </div>
              <div className="list-field"><label>About your PG</label><textarea placeholder="Describe your PG..." value={form.about} onChange={handleInput('about')} rows={3} /></div>
            </div>
          )}
          {step === 3 && (
            <div className="list-success">
              <div className="list-success-icon">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="18" fill="#DCFCE7" />
                  <path d="M10 18.5L15.5 24L26 13" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="list-success-title">Listing Submitted!</h3>
              <p className="list-success-msg">Thanks, <strong>{form.name}</strong>! We've received your PG listing for <strong>{form.pgName}</strong>. Our team will review and contact you at {form.email} within 24–48 hours.</p>
              <button className="list-submit-btn" onClick={onClose}>Done</button>
            </div>
          )}
          {error && <p className="list-error">{error}</p>}
        </div>
        {step < 3 && (
          <div className="list-modal-footer">
            {step === 2 ? <button className="list-back-btn" onClick={() => { setStep(1); setError('') }}>← Back</button> : <div />}
            <button className="list-submit-btn" onClick={() => {
              if (step === 1) { const err = validateStep1(); if (err) { setError(err); return } setError(''); setStep(2) }
              else { const err = validateStep2(); if (err) { setError(err); return } setStep(3) }
            }}>
              {step === 1 ? 'Next →' : 'Submit Listing'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Navbar ───────────────────────────────────────────────────────
const Navbar = () => {
  const { isLoggedIn, user, logout } = useAuth()
  const { showAuth, setShowAuth, showListPG, setShowListPG } = useUI()
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const navRef = useRef(null)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  useEffect(() => {
    if (!menuOpen) return
    const handleOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [menuOpen])

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setMenuOpen(false) }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('loginResponse')
    localStorage.removeItem('userDataList')
    localStorage.clear()
    logout()
    navigate('/')
    setMenuOpen(false)
  }

  const closeMobileMenu = () => {
    setMenuOpen(false)
  }

const Actions = () => isLoggedIn ? (
  <>
    <span className="navbar-greeting">Hi, {user.name.split(' ')[0]}</span>
    {user.type === 'pg' && (
      <button className="navbar-dashboard-btn" onClick={() => navigate('/owner-dashboard')}>
        {/* ... dashboard icon ... */}
        Dashboard
      </button>
    )}
    {user.type === 'tenant' && (
      <button className="navbar-dashboard-btn" onClick={() => navigate('/tenant-dashboard')}>
        {/* ... dashboard icon ... */}
        My Dashboard
      </button>
    )}
    <button className="navbar-logout-btn" onClick={handleLogout}>
      {/* ... logout icon ... */}
      Logout
    </button>
  </>
) : isMobile ? (
  // ✅ Mobile/Tablet: Show "Login" text
  <button className="navbar-cta" onClick={() => setShowAuth(true)}>
    Login
  </button>
) : (
  // ✅ Desktop: Show icon
  <button className="navbar-cta" onClick={() => setShowAuth(true)}>
    <FaRegCircleUser size={25} color="#F06E18" />
  </button>
)

  return (
    <>
      <nav className="navbar" ref={navRef}>
        <div className="navbar-inner container">
          <div className="navbar-row-main">
            <NavLink to="/" className="navbar-logo" onClick={() => setMenuOpen(false)}>
              <img src="/assets/AapkaPG-logo.svg" alt="Aapka PG Logo" className="od-navbar-logo" />
            </NavLink>

            <div className="navbar-search">
              <SearchBar compact={true} />
            </div>

            <div className="navbar-actions">
              <Actions />
              <button
                className={`navbar-hamburger ${menuOpen ? 'open' : ''}`}
                onClick={() => setMenuOpen(p => !p)}
                aria-label="Toggle menu"
              >
                <span /><span /><span />
              </button>
            </div>
          </div>
        </div>

        <div className={`navbar-mobile-menu ${menuOpen ? 'visible' : ''}`}>
          <div className="mobile-search-wrapper">
            <SearchBar compact={true} onAction={closeMobileMenu} />
          </div>
            {isLoggedIn ? (
              <>
                {user.type === 'pg' && (
                  <button className="mobile-cta" onClick={() => { setMenuOpen(false); navigate('/owner-dashboard') }}>
                    Go to Dashboard
                  </button>
                )}
                {user.type === 'tenant' && (
                  <button className="mobile-cta" onClick={() => { setMenuOpen(false); navigate('/tenant-dashboard') }}>
                    My Dashboard
                  </button>
                )}
                <button className="mobile-list-btn" onClick={() => { setMenuOpen(false); handleLogout() }}>
                  Logout
                </button>
              </>
            ) : isMobile ? (
              // ✅ Mobile/Tablet: Show "Login" text
              <button className="mobile-list-btn" onClick={() => { setMenuOpen(false); setShowAuth(true) }}>
                Login
              </button>
            ) : (
              // ✅ Desktop: Show icon
              <button className="navbar-cta mobile-cta" onClick={() => { setMenuOpen(false); setShowAuth(true) }}>
                <FaRegCircleUser size={20} />
              </button>
            )}
        </div>
      </nav>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showListPG && <ListPGModal onClose={() => setShowListPG(false)} />}
    </>
  )
}

export default Navbar

